import type { ViewController } from '@presentation/shell/NexxusShell';
import type { Salarie } from '@shared/types/Salarie';
import { fmtFCFA, esc } from '@presentation/shell/format';
import { ToastService } from '@presentation/components/Toast';
import { canMutateRecords } from '@application/access/ViewAccessPolicy';
import { uuid } from '@shared/utils/uuid';
import { plaqueGabon } from '@shared/validation/identifiantsGabon';
import {
  bindCrudTable,
  crudActions,
  loadRegister,
  openRegisterForm,
  removeById,
  saveRegister,
  tenantIdOf,
  upsertById,
  type FieldDef,
} from '@presentation/shell/localCrud';

/** Marques courantes (seed) pour la flotte gabonaise. */
export const MARQUES_FLOTTE_SEED = [
  'Toyota',
  'Nissan',
  'Hyundai',
  'Kia',
  'Mitsubishi',
  'Ford',
  'Peugeot',
  'Renault',
  'Mercedes-Benz',
  'Autre',
] as const;

export interface VehiculeLocal {
  id: string;
  immatriculation: string;
  marque: string;
  modele: string;
  type: string;
  salarie_id: string;
  date_affectation: string;
  valeur: number;
  avantage_nature_mensuel: number;
  statut: string;
}

function guard(can: boolean): boolean {
  if (can) return true;
  ToastService.warning('Action réservée au gestionnaire RH et à l’administrateur');
  return false;
}

function kpi(label: string, value: string): string {
  return `<div class="kpi"><div class="label">${label}</div><div class="value">${value}</div></div>`;
}

function salarieOptions(salaries: Salarie[]): { value: string; label: string }[] {
  const actifs = salaries.filter((s) => s.statut !== 'sortie');
  return [
    { value: '', label: '— Non affecté —' },
    ...actifs.map((s) => ({ value: s.id, label: `${s.prenom} ${s.nom}` })),
  ];
}

function salarieLabel(salaries: Salarie[], id: string): string {
  const s = salaries.find((x) => x.id === id);
  return s ? `${s.prenom} ${s.nom}` : 'Non affecté';
}

function statutPill(statut: string): string {
  if (statut === 'En service') return '<span class="pill success">En service</span>';
  if (statut === 'Hors service') return '<span class="pill danger">Hors service</span>';
  return `<span class="pill warning">${esc(statut)}</span>`;
}

export const flotteController: ViewController = async (section, ctx, shell) => {
  const tid = tenantIdOf(ctx);
  const canMutate = canMutateRecords(shell.permission);
  const salaries = await ctx.salaries.list();
  let items = await loadRegister<VehiculeLocal>(tid, 'rh.vehicules');

  const render = (): void => {
    const enService = items.filter((v) => v.statut === 'En service');
    const totAvantage = enService.reduce((s, v) => s + (v.avantage_nature_mensuel || 0), 0);
    const grid = section.querySelector('#flotte-kpi-grid');
    if (grid) {
      grid.innerHTML =
        kpi('Véhicules', String(items.length)) +
        kpi('En service', String(enService.length)) +
        kpi('Avantages en nature / mois', fmtFCFA(totAvantage));
    }
    const tbody = section.querySelector('#flotte-table tbody');
    const empty = section.querySelector<HTMLElement>('#flotte-empty');
    if (empty) empty.style.display = items.length === 0 ? '' : 'none';
    if (!tbody) return;
    tbody.innerHTML =
      items.length === 0
        ? ''
        : items
            .map(
              (v) => `<tr>
          <td class="mono">${esc(v.immatriculation || '—')}</td>
          <td><strong>${esc(v.marque)} ${esc(v.modele)}</strong></td>
          <td>${esc(v.type || '—')}</td>
          <td>${esc(salarieLabel(salaries, v.salarie_id))}</td>
          <td class="num">${esc(fmtFCFA(v.valeur))}</td>
          <td class="num">${esc(fmtFCFA(v.avantage_nature_mensuel))}</td>
          <td>${statutPill(v.statut)}</td>
          <td class="num">${canMutate ? crudActions(v.id) : '—'}</td>
        </tr>`,
            )
            .join('');
  };
  render();

  const fields = (): FieldDef[] => [
    {
      kind: 'text',
      name: 'immatriculation',
      label: 'Immatriculation',
      required: true,
      placeholder: 'AB-789-CD',
    },
    {
      kind: 'select',
      name: 'marque',
      label: 'Marque',
      options: MARQUES_FLOTTE_SEED.map((m) => ({ value: m, label: m })),
    },
    { kind: 'text', name: 'modele', label: 'Modèle' },
    {
      kind: 'select',
      name: 'type',
      label: 'Type',
      options: [
        { value: 'Berline', label: 'Berline' },
        { value: '4x4 / SUV', label: '4x4 / SUV' },
        { value: 'Utilitaire', label: 'Utilitaire' },
        { value: 'Pick-up', label: 'Pick-up' },
        { value: 'Moto', label: 'Moto' },
        { value: 'Autre', label: 'Autre' },
      ],
    },
    {
      kind: 'select',
      name: 'salarie_id',
      label: 'Affecté à (salarié)',
      options: salarieOptions(salaries),
    },
    { kind: 'date', name: 'date_affectation', label: "Date d'affectation" },
    { kind: 'number', name: 'valeur', label: 'Valeur (FCFA)', min: 0 },
    {
      kind: 'number',
      name: 'avantage_nature_mensuel',
      label: 'Avantage en nature mensuel (FCFA)',
      min: 0,
    },
    {
      kind: 'select',
      name: 'statut',
      label: 'Statut',
      options: [
        { value: 'En service', label: 'En service' },
        { value: 'En réparation', label: 'En réparation' },
        { value: 'Hors service', label: 'Hors service' },
      ],
    },
  ];

  const openEditor = (existing?: VehiculeLocal): void => {
    if (!guard(canMutate)) return;
    openRegisterForm({
      parent: section,
      title: existing ? 'Modifier le véhicule' : 'Nouveau véhicule',
      fields: fields(),
      values: existing,
      onSubmit: async (data) => {
        const plaqueParsed = plaqueGabon.safeParse(String(data['immatriculation'] ?? ''));
        if (!plaqueParsed.success) {
          ToastService.error(plaqueParsed.error.issues[0]?.message ?? 'Immatriculation invalide');
          throw new Error('immatriculation_invalide');
        }
        let marque = String(data['marque'] ?? '');
        if (!(MARQUES_FLOTTE_SEED as readonly string[]).includes(marque)) {
          marque = 'Autre';
        }
        const row: VehiculeLocal = {
          id: existing?.id ?? uuid('veh'),
          immatriculation: plaqueParsed.data,
          marque,
          modele: String(data['modele'] ?? ''),
          type: String(data['type'] ?? 'Berline'),
          salarie_id: String(data['salarie_id'] ?? ''),
          date_affectation: String(data['date_affectation'] ?? ''),
          valeur: Number(data['valeur'] ?? 0),
          avantage_nature_mensuel: Number(data['avantage_nature_mensuel'] ?? 0),
          statut: String(data['statut'] ?? 'En service'),
        };
        items = upsertById(items, row);
        await saveRegister(tid, 'rh.vehicules', items);
        render();
      },
    });
  };

  bindCrudTable(
    section.querySelector('#flotte-table tbody'),
    section,
    (id) => openEditor(items.find((x) => x.id === id)),
    async (id) => {
      items = removeById(items, id);
      await saveRegister(tid, 'rh.vehicules', items);
      render();
    },
    canMutate,
  );

  const w = window as unknown as Record<string, unknown>;
  w['openVehiculeModal'] = () => openEditor();

  const btn = section.querySelector<HTMLElement>('#btn-new-vehicule');
  if (btn) btn.style.display = canMutate ? '' : 'none';
};
